import {AfterViewInit, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {WeaveStructure} from '@domain/textile-data/weave-structure/weave-structure';
import {WeaveStructureForMachineQuality} from '@domain/textile-data/weave-structure/weave-structure-for-machine-quality';
import {GetAllWeaveStructuresParameters, WEAVE_STRUCTURES, WeaveStructures} from '@infrastructure/http/weave-structure/weave-structures';
import {
  AgGridRowSelection,
  AssertionUtils,
  BaseComponent,
  DialogComponentData,
  FilterModelType,
  FilterType,
  GridModel,
  GridModelBuilder,
  GridModelBuilderFactoryService,
  GridOptionsBuilderFactoryService,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  SelectGridDialog
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {FirstDataRenderedEvent, GridApi, GridOptions, RowDataUpdatedEvent, RowNode, RowSelectedEvent} from 'ag-grid-community';
import {takeUntil} from 'rxjs/operators';
import {OverviewListWeaveStructureService} from '../../weave-structure/overview/overview-list-weave-structure.service';

@Component({
  templateUrl: './select-weave-structure.component.html'
})
export class SelectWeaveStructureComponent extends BaseComponent implements OnInit, AfterViewInit, SelectGridDialog {
  @ViewChild('weaveStructuresGrid') public weaveStructuresGrid: AgGridAngular;
  public rowSelection: AgGridRowSelection;
  public listOfWeaveStructures: WeaveStructureForMachineQuality[];
  public listOfGridOptions: GridOptions[];
  public listOfGridApis: GridApi[];
  public objectTranslationKey: string;
  public objectTranslationKeyCount: number;

  private selectedWeaveStructures: WeaveStructure[];
  private numberOfCreelPositions: number;

  public constructor(
    @Inject(WEAVE_STRUCTURES) private readonly weaveStructures: WeaveStructures,
    private readonly dialogRef: MatDialogRef<SelectWeaveStructureComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      numberOfCreelPositions: number;
      objectTranslationKey: string;
      filterName: string;
      selectMultiple: AgGridRowSelection;
      weaveStructures: WeaveStructure[];
      filterItems: (wvs: WeaveStructureForMachineQuality[]) => WeaveStructureForMachineQuality[];
    },
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly overviewListWeaveStructureService: OverviewListWeaveStructureService,
    private readonly gridModelBuilderFactory: GridModelBuilderFactoryService,
    private readonly navigationHelperService: NavigationHelperService<DialogComponentData<typeof SelectWeaveStructureComponent>>
  ) {
    super();
    this.selectedWeaveStructures = data?.weaveStructures ?? [];
    this.numberOfCreelPositions = data?.numberOfCreelPositions ?? null;
    this.rowSelection = data?.selectMultiple ?? AgGridRowSelection.SINGLE;
    this.objectTranslationKey = data?.objectTranslationKey ?? 'TEXTILE_DATA.WEAVE_STRUCTURE.WEAVE_STRUCTURE';
    this.objectTranslationKeyCount = this.rowSelection === AgGridRowSelection.MULTIPLE ? 2 : 1;
  }

  public get gridOptionsForListOfWeaveStructures(): GridOptions {
    return this.listOfGridOptions[0];
  }

  public ngOnInit(): void {
    this.setGridOptionsForListOfWeaveStructures();
    this.getAll(true);
  }

  public ngAfterViewInit(): void {
    this.listOfGridApis = [this.weaveStructuresGrid?.api];
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<DialogComponentData<typeof SelectWeaveStructureComponent>>({dialogComponent: SelectWeaveStructureComponent});
  }

  public selectWeaveStructure(): void {
    this.dialogRef.close(this.isMultiRowSelectionAllowed() ? this.selectedWeaveStructures : [this.weaveStructuresGrid.api.getSelectedRows()[0]]);
  }

  public canSelectWeaveStructure(): boolean {
    return !AssertionUtils.isEmpty(this.weaveStructuresGrid?.api?.getSelectedRows());
  }

  public isMultiRowSelectionAllowed(): boolean {
    return this.rowSelection === AgGridRowSelection.MULTIPLE;
  }

  public showOnlyLatestVersionChanged(change: boolean): void {
    this.getAll(change);
  }

  private setGridOptionsForListOfWeaveStructures(): void {
    this.listOfGridOptions = [
      this.gridOptionsBuilderFactoryService
        .getBuilder(this.overviewListWeaveStructureService.getColumnDefs(false, false, true), GridIdentifier.SELECT_WEAVE_STRUCTURE)
        .withRowSelection(this.isMultiRowSelectionAllowed())
        .withOnRowSelected((event: RowSelectedEvent) => this.updateSelectedWeaveStructures(event))
        .withOnCellDoubleClicked(() => this.selectWeaveStructure())
        .withOnRowDataUpdated(({api}: RowDataUpdatedEvent) => this.selectSelectedWeaveStructures(api))
        .withNoRowsOverlay({
          scale: 0.7,
          titleParam: 'TEXTILE_DATA.WEAVE_STRUCTURE.WEAVE_STRUCTURE',
          hideDescription: true
        } as NoDataOverlayComponentParams)
        .withLoadingOverlay({
          scale: 0.7
        } as OverlayComponentParams)
        .withRememberStateForNavigationHelper()
        .withOnFirstDataRendered(({api}: FirstDataRenderedEvent) => {
          this.selectSelectedWeaveStructures(api);
        })
        .build()
    ];
  }

  private selectSelectedWeaveStructures(gridApi: GridApi): void {
    gridApi.forEachNode((node: RowNode) => {
      node.setSelected(this.isWeaveStructureSelected(node));
    });
  }

  private isWeaveStructureSelected(data: RowSelectedEvent | RowNode): boolean {
    return this.selectedWeaveStructures.some((selectedWeaveStructure: WeaveStructure) => {
      return selectedWeaveStructure.id === data.data.id;
    });
  }

  private updateSelectedWeaveStructures(event: RowSelectedEvent): void {
    if (!event.node.isSelected() && this.listOfWeaveStructures.some((weaveStructure: WeaveStructureForMachineQuality) => weaveStructure.id === event.data.id)) {
      this.selectedWeaveStructures = this.selectedWeaveStructures.filter((currentSelectedWeaveStructure: WeaveStructure) => currentSelectedWeaveStructure.id !== event.data.id);
    } else {
      if (!this.isWeaveStructureSelected(event)) {
        this.selectedWeaveStructures.push(event.data);
      }
    }
  }

  private getAll(showOnlyLatestVersion: boolean): void {
    const getAllWeaveStructuresParameters: Partial<GetAllWeaveStructuresParameters> = {numberOfCreelPositions: this.numberOfCreelPositions, showOnlyLatestVersion: showOnlyLatestVersion};
    const gridModel: GridModel = this.getGridFilterModel(this.objectTranslationKey, this.data.filterName);

    this.weaveStructures
      .get(gridModel, getAllWeaveStructuresParameters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfWeaveStructures: WeaveStructureForMachineQuality[]) => {
        this.listOfWeaveStructures = this.data.filterItems ? this.data.filterItems(listOfWeaveStructures) : listOfWeaveStructures;
      });
  }

  private getGridFilterModel(type: string, wvsName: string): GridModel {
    const filterModel: GridModelBuilder = this.gridModelBuilderFactory.getBuilder();

    if (type.includes('SINGLE_WIDTH_WEAVE_STRUCTURE')) {
      this.setFilterForWidthType(filterModel, 'SINGLE_WIDTH');
    } else if (type.includes('BOTTOM_WEAVE_STRUCTURE')) {
      this.setFilterForWidthType(filterModel, 'BOTTOM');
    } else if (type.includes('TOP_WEAVE_STRUCTURE')) {
      this.setFilterForWidthType(filterModel, 'TOP');
    } else if (type.includes('BACKGROUND_WEAVE_STRUCTURE')) {
      this.setFilterForWidthType(filterModel, 'BACKGROUND');
    }

    if (wvsName) {
      filterModel.newFilter('name', this.gridModelBuilderFactory.getFilterBuilder(FilterModelType.TEXT).setType(FilterType.EQUALS).setFilter(wvsName));
    }

    return filterModel.build();
  }

  private setFilterForWidthType(filterModel: GridModelBuilder, value: string): void {
    filterModel.newFilter(
      'widthType',
      this.gridModelBuilderFactory.getFilterBuilder(FilterModelType.MULTI).newFilter(this.gridModelBuilderFactory.getFilterBuilder(FilterModelType.SET).setValues([`${value}`]))
    );
  }
}
